import { useState, useEffect } from 'react';
import './Home.css';
import { Link } from 'react-router-dom';
import heartIcon from '../images/heart.png';
import lysosomeIcon from '../images/lysosome.png';
import pkuIcon from '../images/PKUmolecule.png';
import diabetesIcon from '../images/Diabetes.png';
import galactosemiaIcon from '../images/Galactosemia.png';

function Home() {
  const [checkedDiseases, setCheckedDiseases] = useState(() => {
    const saved = localStorage.getItem('checkedDiseases');
    return saved ? JSON.parse(saved) : {};
  });
  
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState(() => {
    const saved = localStorage.getItem('comments');
    return saved ? JSON.parse(saved) : [];
  });

  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    localStorage.setItem('checkedDiseases', JSON.stringify(checkedDiseases));
  }, [checkedDiseases]);

  useEffect(() => {
    localStorage.setItem('comments', JSON.stringify(comments));
  }, [comments]);

  useEffect(() => {
    const container = document.querySelector('.disease-cards');
    
    const updateScrollIndicator = () => {
      const maxScroll = container.scrollWidth - container.clientWidth;
      const currentScroll = container.scrollLeft;
      const scrollPercentage = (currentScroll / maxScroll) * (200); // Subtract indicator width
      setScrollPosition(scrollPercentage);
    };

    container.addEventListener('scroll', updateScrollIndicator);
    window.addEventListener('resize', updateScrollIndicator);
    
    // Initial update
    updateScrollIndicator();

    return () => {
      container.removeEventListener('scroll', updateScrollIndicator);
      window.removeEventListener('resize', updateScrollIndicator);
    };
  }, []);

  const diseases = [
    { name: 'Atherosclerosis', sections: ['Pathogenesis', 'Signs & Symptoms', 'Prognosis', 'Risk Factors', 'Diagnosis & Treatment', 'Learning Tools'] },
    { name: 'Tay-Sachs Disease', sections: ['Pathogenesis', 'Signs & Symptoms', 'Prognosis', 'Risk Factors', 'Diagnosis & Treatment', 'Learning Tools'] },
    { name: 'Gaucher Disease', sections: ['Pathogenesis', 'Signs & Symptoms', 'Prognosis', 'Risk Factors', 'Diagnosis & Treatment', 'Learning Tools'] },
    { name: 'Niemann-Pick Disease', sections: ['Pathogenesis', 'Signs & Symptoms', 'Prognosis', 'Risk Factors', 'Diagnosis & Treatment', 'Learning Tools'] },
    { name: 'Phenylketonuria (PKU)', sections: ['Pathogenesis', 'Signs & Symptoms', 'Prognosis', 'Risk Factors', 'Diagnosis & Treatment', 'Learning Tools'] },
    { name: 'Diabetes Mellitus', sections: ['Pathogenesis', 'Signs & Symptoms', 'Prognosis', 'Risk Factors', 'Diagnosis & Treatment', 'Learning Tools'] },
    { name: 'Galactosemia', sections: ['Pathogenesis', 'Signs & Symptoms', 'Prognosis', 'Risk Factors', 'Diagnosis & Treatment', 'Learning Tools'] },
  ];

  const handleCheck = (disease, section) => {
    setCheckedDiseases(prev => ({
      ...prev,
      [disease]: {
        ...prev[disease],
        [section]: !prev[disease]?.[section]
      }
    }));
  };

  const handleSubmitComment = (e) => {
    e.preventDefault();
    if (comment.trim() && rating > 0) {
      const newComment = {
        rating,
        text: comment,
        date: new Date().toLocaleDateString()
      };
      setComments(prev => [...prev, newComment]);
      setComment('');
      setRating(0);
    }
  };

  const handleStarClick = (clickedRating) => {
    if (rating === clickedRating) {
      setRating(0);
    } else {
      setRating(clickedRating);
    }
  };

  const handleDeleteComment = (commentIndex) => {
    const newComments = comments.filter((_, index) => index !== commentIndex);
    setComments(newComments);
    localStorage.setItem('comments', JSON.stringify(newComments));
  };

  return (
    <div className="home">
      <h1>Welcome to Metabolopedia!</h1>
      <p className="mission-statement">
        Our team aims to create an informational website outlining and teaching metabolic disease 
        at the level of pre-medical undergraduate students. We will explore predisposing factors, 
        underlying biochemical mechanisms, significance, major manifestations, treatment options, 
        and pathogenesis.
      </p>
      <br></br>

      <h1>Learning Outcomes</h1>
      <div className="learning-outcomes">
        <div className="outcome-card">
          <div className="outcome-icon">🌍</div>
          <p>Assess the burden of metabolic diseases to understand their impact on public health.</p>
        </div>

        <div className="outcome-card">
          <div className="outcome-icon">🔬</div>
          <p>Explain the <strong>pathogenesis</strong> and <strong>etiologies</strong> of metabolic diseases to comprehend the underlying mechanisms and identify the origins.</p>
        </div>

        <div className="outcome-card">
          <div className="outcome-icon">📊</div>
          <p>Summarize the <strong>prognoses</strong> of these metabolic diseases, considering factors influencing disease progression to anticipate patient outcomes.</p>
        </div>

        <div className="outcome-card">
          <div className="outcome-icon">⚠️</div>
          <p>Identify key <strong>risk factors</strong> associated with these metabolic diseases and explain their clinical significance to facilitate prevention strategies.</p>
        </div>

        <div className="outcome-card">
          <div className="outcome-icon">🔍</div>
          <p>Explain the <strong>diagnostic criteria</strong> and tools used to identify these metabolic diseases, emphasizing their roles in accurate diagnosis.</p>
        </div>

        <div className="outcome-card">
          <div className="outcome-icon">💊</div>
          <p>Describe current <strong>treatment strategies</strong> and their mechanisms for these metabolic diseases, evaluating their effectiveness and limitations to inform clinical decision-making.</p>
        </div>
      </div>

      <section className="progress-tracker">
        <h2>Track Your Learning Progress</h2>
        <p>Check off sections as you complete your study of each disease:</p>
        <div className="disease-checklist">
          {diseases.map(disease => (
            <div key={disease.name} className="disease-item">
              <h3>{disease.name}</h3>
              {disease.sections.map(section => (
                <label key={section} className="section-checkbox">
                  <input
                    type="checkbox"
                    checked={checkedDiseases[disease.name]?.[section] || false}
                    onChange={() => handleCheck(disease.name, section)}
                  />
                  {section}
                </label>
              ))}
            </div>
          ))}
        </div>
      </section>

      <section className="disease-gallery">
        <h2>Exploring Metabolic Diseases</h2>
        <div className="metabolic-definition">
          <h4>ACCORDING TO ANIMAL BIOTECHNOLOGY: MODELS IN DISCOVERY AND TRANSLATION, 2ND EDITION</h4>
          <p>A metabolic disorder is a condition that occurs when the normal metabolic pathways are disturbed, which results in either increased or decreased metabolite in the tissue or its secretions, which often leads to the development of the disease or its progression.<sup>1</sup></p>
        </div>
        <div className="disease-cards-container">
          <button className="scroll-button left" onClick={() => document.querySelector('.disease-cards').scrollBy(-300, 0)}>
            ←
          </button>
          
          <div className="disease-cards">
            <div className="disease-card">
              <img src={heartIcon} alt="Heart Icon" className="card-icon" />
              <h3>ATHEROSCLEROSIS</h3>
              <Link to="/atherosclerosis/pathogenesis" className="explore-link">EXPLORE →</Link>
            </div>

            <div className="disease-card">
              <img src={lysosomeIcon} alt="Lysosome Icon" className="card-icon" />
              <h3>LYSOSOMAL STORAGE DISEASE</h3>
              <Link to="/tay-sachs/pathogenesis" className="explore-link">EXPLORE →</Link>
            </div>

            <div className="disease-card">
              <img src={pkuIcon} alt="PKU Molecule Icon" className="card-icon" />
              <h3>PHENYLKETONURIA</h3>
              <Link to="/pku/pathogenesis" className="explore-link">EXPLORE →</Link>
            </div>

            <div className="disease-card">
              <img src={diabetesIcon} alt="Diabetes Icon" className="card-icon" />
              <h3>DIABETES MELLITUS</h3>
              <Link to="/diabetes/pathogenesis" className="explore-link">EXPLORE →</Link>
            </div>

            <div className="disease-card">
              <img src={galactosemiaIcon} alt="Galactosemia Icon" className="card-icon" />
              <h3>GALACTOSEMIA</h3>
              <Link to="/galactosemia/pathogenesis" className="explore-link">EXPLORE →</Link>
            </div>
          </div>

          <button className="scroll-button right" onClick={() => document.querySelector('.disease-cards').scrollBy(300, 0)}>
            →
          </button>
        </div>
        <div className="scroll-indicator">
          <div 
            className="scroll-track" 
            style={{ 
              width: '33.33%',
              transform: `translateX(${scrollPosition}%)` 
            }}
          ></div>
        </div>
      </section>

      <section className="feedback-section">
        <h2>Our Mission</h2>

        <div className="pulse-section">
          <h3>To utilize the PULSE project to help future learners master the content in Path3500!</h3>
        </div>
        <div className="mission-content">
          <p>Welcome to Metabolopedia, a learning hub for metabolic diseases. Through this website, you will gain insight into the biological underpinnings and clinical relevance of diseases such as atherosclerosis, lysosomal storage diseases, phenylketonuria, diabetes, and galactosemia.</p>
          
          <p>Designed with Pathology 3500 learners in mind, this website aims to prepare you for the real-world challenges you may face in your career by providing a clear, comprehensive, and engaging approach to one of the course's more difficult topics. Our passion for this subject inspired us to translate dense and often overwhelming material into a learner-friendly format that fosters confidence, motivation, and curiosity.</p>
          
          <p>With self-paced modules, interactive tools, and accessible content available across devices, this website empowers you to take control of your learning anytime, anywhere. You'll also benefit from features like progress tracking, outcome-based learning checks, and real-time feedback opportunities—ensuring your experience is as impactful as it is adaptable.</p>
          
          <p>Join us as we explore metabolic diseases and support your journey toward academic success.</p>
        </div>

        <h2>How do you think we did?</h2>
        <form onSubmit={handleSubmitComment} className="feedback-form">
          <div className="rating-stars">
            {[1, 2, 3, 4, 5].map(star => (
              <button
                key={star}
                type="button"
                className={`star ${rating >= star ? 'active' : ''}`}
                onClick={() => handleStarClick(star)}
              >
                ★
              </button>
            ))}
          </div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Share your thoughts about this resource..."
            rows="4"
          />
          <button type="submit" className="submit-button">Submit Feedback</button>
        </form>
        
        <div className="comments-section">
          <h3>Recent Comments</h3>
          {comments.length > 0 ? (
            comments.map((c, i) => (
              <div key={i} className="comment">
                <div className="comment-header">
                  <div className="comment-rating">{'★'.repeat(c.rating)}</div>
                  <button 
                    className="delete-button" 
                    onClick={() => handleDeleteComment(i)}
                    aria-label="Delete comment"
                  >
                    ×
                  </button>
                </div>
                <p>{c.text}</p>
                <span className="comment-date">{c.date}</span>
              </div>
            ))
          ) : (
            <p className="no-comments">Be the first to leave a comment!</p>
          )}
        </div>
      </section>

      <section className="references">
        <h2>References</h2>
        <ol className="no-indent">
          <li>Verma, A. S., & Singh, A. (2020). Enzyme inhibition assay for metabolic disorders—exploring leads from medicinal plants. In Animal Biotechnology (2nd ed.). essay, Elsevier Inc.</li>
          <li>Kumar, V., Abbas, A. K., Aster, J. C., & Deyrup, A. T. (Eds.). (2022). Robbins & Kumar Basic Pathology (11th ed.). Elsevier Health Sciences. https://pageburstls.elsevier.com/books/9780323790222</li>
          <li>Tugaleva, E. (2025, January). Atherosclerosis and Hypertension. Pathology 3500. London.</li>
          <li>Mayo Foundation for Medical Education and Research. (2024, September 20). Arteriosclerosis / atherosclerosis. Mayo Clinic. https://www.mayoclinic.org/diseases-conditions/arteriosclerosis-atherosclerosis/diagnosis-treatment/drc-20350575</li>
          <li>Atherosclerosis - Cardiovascular - Medbullets Step 1. https://step1.medbullets.com/cardiovascular/108038/atherosclerosis.</li>
          <li>NHLBI. (2022, June 17). Learn About the Causes of Atherosclerosis [Video]. Vimeo. https://vimeo.com/721557370.</li>
          <li>Macrophage profiling in atherosclerosis: understanding the unstable plaque | Request PDF. ResearchGate (2024) doi:10.1007/s00395-023-01023-z.</li>
          <li>Darling, M. (2024, November). Metabolic and Nutritional Diseases. Pathology 3500. London.</li>
          <li>Clarke, J.T.R. (2008) Lysosomal storage disorders - symptoms, causes, treatment: Nord, National Organization for Rare Disorders. Available at: https://rarediseases.org/rare-diseases/lysosomal-storage-disorders/ (Accessed: 31 March 2025).</li>
          <li>Perko, D. et al. Comparison of Tandem Mass Spectrometry and the Fluorometric Method—Parallel Phenylalanine Measurement on a Large Fresh Sample Series and Implications for Newborn Screening for Phenylketonuria. Int J Mol Sci 24, 2487 (2023).</li>
          <li>Phenylketonuria (2025) Wikipedia. Available at: https://en.wikipedia.org/wiki/Phenylketonuria (Accessed: 31 March 2025).</li>
          <li>Phenylketonuria (PKU) (2022) Mayo Clinic. Available at: https://www.mayoclinic.org/diseases-conditions/phenylketonuria/symptoms-causes/syc-20376302 (Accessed: 31 March 2025).</li>
          <li>Ashe, K. et al. Psychiatric and Cognitive Aspects of Phenylketonuria: The Limitations of Diet and Promise of New Treatments. Front Psychiatry 10, 561 (2019).</li>
          <li>Zhu, M. The Guthrie Test for Early Diagnosis of Phenylketonuria | Embryo Project Encyclopedia. 2017-03-30.</li>
          <li>Howlett, C. (2025, February). Endocrine System. Pathology 3500. London.</li>
          <li>World Health Organization. (2024, November 14). Diabetes. World Health Organization. https://www.who.int/news-room/fact-sheets/detail/diabetes</li>
          <li>Diabetes (2025) Cleveland Clinic. Available at: https://my.clevelandclinic.org/health/diseases/7104-diabetes (Accessed: 31 March 2025).</li>
          <li>Khin, P. P., Lee, J. H. & Jun, H.-S. Pancreatic Beta-cell Dysfunction in Type 2 Diabetes. Eur J Inflamm 21, 1721727X231154152 (2023).</li>
          <li>Banday, M.Z., Sameer, A.S. and Nissar, S. (2020) Pathophysiology of diabetes: An overview, Avicenna journal of medicine. Available at: https://pmc.ncbi.nlm.nih.gov/articles/PMC7791288/ (Accessed: 31 March 2025).</li>
          <li>Demczko, M. (2024) Galactosemia - Children's health issues, Merck Manual Consumer Version. Available at: https://www.merckmanuals.com/en-ca/home/children-s-health-issues/hereditary-metabolic-disorders/galactosemia (Accessed: 31 March 2025).</li>
          <li>Zupan, M., Berry, G.T. and Levy, H. (2023) Galactosemia - symptoms, causes, treatment: Nord, National Organization for Rare Disorders. Available at: https://rarediseases.org/rare-diseases/galactosemia/ (Accessed: 31 March 2025).</li>
          <li>Galactosemia (no date) Galactosemia | Boston Children's Hospital. Available at: https://www.childrenshospital.org/conditions/galactosemia (Accessed: 31 March 2025).</li>
          <li>Welling, L. et al. International clinical guideline for the management of classical galactosemia: diagnosis, treatment, and follow-up. J Inherit Metab Dis 40, 171–176 (2017).</li>
          <li>Haskovic, M. et al. Pathophysiology and targets for treatment in hereditary galactosemia: A systematic review of animal and cellular models. J Inherit Metab Dis 43, 392–408 (2020).</li>
          <li>Craig McCabe, J.R. (2023) The pathophysiology of galactosemia, CheckRare. Available at: https://checkrare.com/the-pathophysiology-of-galactosemia/ (Accessed: 31 March 2025).</li>
          <li>Whelton, P. K. et al. 2017 ACC/AHA/AAPA/ABC/ACPM/AGS/APhA/ASH/ASPC/NMA/PCNA Guideline for the Prevention, Detection, Evaluation, and Management of High Blood Pressure in Adults: A Report of the American College of Cardiology/American Heart Association Task Force on Clinical Practice Guidelines. Hypertension 71, (2018).</li>
          <li>Arnett, D. K. et al. 2019 ACC/AHA Guideline on the Primary Prevention of Cardiovascular Disease: A Report of the American College of Cardiology/American Heart Association Task Force on Clinical Practice Guidelines. Circulation 140, (2019).</li>
          <li>Lloyd-Jones, D. M. et al. Life's Essential 8: Updating and Enhancing the American Heart Association's Construct of Cardiovascular Health: A Presidential Advisory From the American Heart Association. Circulation 146, (2022).</li>
          <li>Pulok K. Mukherjee, Shiv Bahadur, Joydeb Chanda, Sayan Biswas, & Subhadip Banerjee. Chapter 31 - Enzyme inhibition assay for metabolic disorders—exploring leads from medicinal plants. 631–653 (2020) doi:https://doi.org/10.1016/B978-0-12-811710-1.00033-1.</li>
          <li>Wilson, P. W. F. et al. Systematic Review for the 2018 AHA/ACC/AACVPR/AAPA/ABC/ACPM/ADA/AGS/APhA/ASPC/NLA/PCNA Guideline on the Management of Blood Cholesterol. Journal of the American College of Cardiology 73, 3210–3227 (2019).</li>
        </ol>
      </section>

    </div>
  );
}

export default Home;